import type { ReactNode } from 'react'

import Stack from '@mui/material/Stack'
import type { SxProps } from '@mui/material/styles'

import { Text } from '@mxenabled/mxui'

import LogoTile from './LogoTile'

interface P2PLogoStackProps {
  /** Brand/merchant logo visuals (rendered as shadowed, cascading tiles). */
  logos: ReactNode[]
  /**
   * Total number of merchants. When it exceeds the rendered logos, the last
   * tile becomes a "+N" more indicator. Defaults to `logos.length`.
   */
  total?: number
  /** Logo tile size in px (Figma: 64). */
  size?: number
  /**
   * Fixed height of the cascading row in px (Figma `Logos` frame: 106). Gives
   * the staircase `mt` offsets room without collapsing.
   */
  rowHeight?: number
  /**
   * Optional status element rendered below the logos (Figma: the full-width
   * "Categorized" `<StatusPill>` in P2P's categorized state). Pass a
   * `<StatusPill sx={{ alignSelf: 'stretch', justifyContent: 'center' }} />`.
   */
  status?: ReactNode
  sx?: SxProps
}

// Vertical offsets that produce the diagonal cascade, keyed by tile count.
// Mirrors the shared P2PMultipleCategorization layout.
const MT_OFFSETS: Record<number, number[]> = {
  2: [0, 36],
  3: [0, 18, 36],
  4: [0, 12, 24, 36],
}

/**
 * A cascading row of shadowed brand tiles with an optional "+N" overflow tile
 * (Peer-to-Peer Categorization). Layout math ported locally from v1
 * `shared/P2P/P2PMultipleCategorization`.
 */
const P2PLogoStack = ({
  logos,
  rowHeight = 106,
  size = 64,
  status,
  total,
  sx,
}: P2PLogoStackProps) => {
  const merchantCount = total ?? logos.length
  // At 4+ merchants Figma shows 3 logos + a "+N" overflow tile (so exactly 4
  // renders as 3 + "+1"); below 4, all logos show. Mirrors v1.
  const hasOverflow = merchantCount >= 4
  const tiles = hasOverflow ? logos.slice(0, 3) : logos.slice(0, 4)
  const offsets = MT_OFFSETS[hasOverflow ? 4 : Math.min(Math.max(tiles.length, 2), 3)]
  // Total tiles actually rendered (logos, or 3 logos + the "+N" tile).
  const tileCount = hasOverflow ? tiles.length + 1 : tiles.length

  // The offset shadow takes no layout space, so interior tiles' shadows fall in
  // the row `gap`; only the LAST tile reserves its shadow footprint, else it
  // overlaps the card padding. (Index key is fine: the list is static.)
  const renderTile = (content: ReactNode, index: number, bgcolor: string) => (
    <LogoTile
      // eslint-disable-next-line react/no-array-index-key
      key={index}
      bgcolor={bgcolor}
      borderColor="background.paper"
      reserveShadowSpace={index === tileCount - 1}
      shadow={true}
      size={size}
      sx={{ mt: `${offsets?.[index] ?? 0}px` }}
    >
      {content}
    </LogoTile>
  )

  return (
    <Stack sx={{ gap: 12, width: '100%', ...sx }}>
      {/* Fixed-height row gives the `mt` staircase room to cascade. */}
      <Stack
        sx={{
          flexDirection: 'row',
          gap: hasOverflow ? 8 : 24,
          height: rowHeight,
          justifyContent: hasOverflow ? 'flex-start' : 'center',
        }}
      >
        {tiles.map((logo, index) => renderTile(logo, index, 'background.paper'))}
        {hasOverflow &&
          renderTile(
            // h3-sized span, not a semantic heading, inside a decorative tile.
            <Text
              aria-label={`+${merchantCount - 3} more`}
              component="span"
              sx={{ color: 'common.white' }}
              variant="h3"
            >
              +{merchantCount - 3}
            </Text>,
            3,
            'grey.800',
          )}
      </Stack>
      {status}
    </Stack>
  )
}

export default P2PLogoStack
